<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/External_Components/An_Icon_Component';
    let nextLink= '/Examples/External_Components/Replacing_a_Part_List';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="a-wrapper-with-a-button-1"
  bind:intersecting={visibleById["a-wrapper-with-a-button-1"]}
  onIntersectChange={(visible) => setVisible("a-wrapper-with-a-button-1", visible)}
>A Wrapper with a Button</SectionComponent>
<p>In this part of the example, we’ll create a <strong>custom wrapper component</strong> in Freon.
The component wraps a native Freon property using the</p>
<a href="https://flowbite-svelte.com/" target="_blank">Flowbite Svelte</a> library.
Our example combines a person’s **phone number** with a **button** that can trigger actions using that value.
Here, clicking the button shows the phone number in a snackbar notification—but you could just as well send it to an
external system to initiate a call or validate it against another database.
<hr>
<SectionComponent tag="h2" 
  id="step-1-create-the-svelte-component-2"
  bind:intersecting={visibleById["step-1-create-the-svelte-component-2"]}
  onIntersectChange={(visible) => setVisible("step-1-create-the-svelte-component-2", visible)}
>Step 1: Create the Svelte Component</SectionComponent>
<p>We’ll create <code>PhoneButton.svelte</code>, a wrapper for the phone number that provides a trigger button.
There are three key parts: <strong>Script</strong>, <strong>HTML</strong>, and <strong>CSS</strong>.</p>
<h3 id="the-script-part-1">The Script Part</h3>
<p>Declare the two mandatory props: <code>editor</code> and <code>box</code>.<br>
The <code>box</code> is a <code>NumberWrapperBox</code>, which can wrap a number node
(see <a
  href="/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types#wrapping-primitive-properties-5"
>Wrapping Property Projections of Primitive type</a>).</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L8-L9

// Props
let &#123; editor, box &#125;: FreComponentProps&lt;NumberWrapperBox&gt; = $props();</code></pre>
<p>Define functions to keep the component in sync when the AST or box model changes.<br>
<code>setFocus</code> forwards focus to the wrapped box; <code>refresh</code> updates any model-driven view state.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L16-L21

async function setFocus(): Promise&lt;void&gt; &#123;
    box.childBox.setFocus();
&#125;
const refresh = (why?: string): void =&gt; &#123;
    // do whatever needs to be done to refresh the elements that show information from the model
&#125;;</code></pre>
<p>Register these with the box using reactive lifecycle hooks:</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L22-L29

$effect(() =&gt; &#123;
    box.setFocus = setFocus;
    box.refreshComponent = refresh;
&#125;);

const colorCls: string = 'text-light-base-50 dark:text-dark-base-900 ';
const buttonCls: string =
  'bg-light-base-600 					dark:bg-dark-base-200 ' +</code></pre>
<h3 id="the-html-part-2">The HTML Part</h3>
<p>The HTML contains a wrapper <code>&lt;div&gt;</code> with the rendered phone number and an Flowbite button.
You <strong>cannot</strong> mount a box directly; instead, use Freon’s <code>RenderComponent</code> which renders any known box.
It requires both <code>box</code> and <code>editor</code>—the same props your component receives.</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L33-L36

&lt;/script&gt;

&lt;div class="wrapper"&gt;
    Phone number: &lt;RenderComponent box=&#123;box.childBox&#125; editor=&#123;editor&#125;/&gt;</code></pre>
<p>Add a toast/snackbar that appears when the button is clicked. The message includes the current phone value:</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L38-L43

    &lt;PhoneOutline class="&#123;iconCls&#125;" /&gt;
    &lt;/Button&gt;
&lt;/div&gt;

&#123;#if showToast&#125;
    &lt;Toast color="green" onclick=&#123;() =&gt; showToast = false&#125;&gt;</code></pre>
<h3 id="the-style-part-3">The Style Part</h3>
<p>Basic styling for the wrapper; Flowbite components themselves are themed via</p>
<a href="https://tailwindcss.com/" target="_blank">Tailwind</a> (already configured for the host app).
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte#L45-L52

        &#123;#snippet icon()&#125;
            &lt;PhoneOutline class="&#123;iconCls&#125;" /&gt;
        &#123;/snippet&#125;
    &lt;/Toast&gt;
&#123;/if&#125;

&lt;style&gt;
    .wrapper &#123;</code></pre>
<h3 id="the-complete-component-4">The Complete Component</h3>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/PhoneButton.svelte

&lt;script lang="ts"&gt;
    import &#123; Toast &#125; from "flowbite-svelte";
    import &#123; PhoneOutline &#125; from 'flowbite-svelte-icons';
    import &#123; type FreComponentProps, RenderComponent &#125; from "@freon4dsl/core-svelte";
    import &#123; NumberWrapperBox &#125; from "@freon4dsl/core";
    import &#123; Button &#125; from 'flowbite-svelte';

    // Props
    let &#123; editor, box &#125;: FreComponentProps&lt;NumberWrapperBox&gt; = $props();

    let clicked: number = 0;
    let showToast: boolean = $state(false);

    // The following three functions need to be included for the editor to function properly.
    // Please, set the focus to the first editable/selectable element in this component.
    async function setFocus(): Promise&lt;void&gt; &#123;
        box.childBox.setFocus();
    &#125;
    const refresh = (why?: string): void =&gt; &#123;
        // do whatever needs to be done to refresh the elements that show information from the model
    &#125;;
    $effect(() =&gt; &#123;
        box.setFocus = setFocus;
        box.refreshComponent = refresh;
    &#125;);

    const colorCls: string = 'text-light-base-50 dark:text-dark-base-900 ';
    const buttonCls: string =
      'bg-light-base-600 					dark:bg-dark-base-200 ' +
      'hover:bg-light-base-900 		dark:hover:bg-dark-base-50 ' +
      'border-light-base-100 			dark:border-dark-base-800 ';
    const iconCls: string = 'ms-0 inline h-6 w-6';
&lt;/script&gt;

&lt;div class="wrapper"&gt;
    Phone number: &lt;RenderComponent box=&#123;box.childBox&#125; editor=&#123;editor&#125;/&gt;
    &lt;Button tabindex=&#123;-1&#125; id="about-button" class="&#123;buttonCls&#125; &#123;colorCls&#125; " name="ToastOpen" onclick=&#123;() =&gt; &#123;clicked++; showToast = true&#125;&#125;&gt;
    &lt;PhoneOutline class="&#123;iconCls&#125;" /&gt;
    &lt;/Button&gt;
&lt;/div&gt;

&#123;#if showToast&#125;
    &lt;Toast color="green" onclick=&#123;() =&gt; showToast = false&#125;&gt;
        This person has been called on number &#123;box.getPropertyValue()&#125;.
        &#123;#snippet icon()&#125;
            &lt;PhoneOutline class="&#123;iconCls&#125;" /&gt;
        &#123;/snippet&#125;
    &lt;/Toast&gt;
&#123;/if&#125;

&lt;style&gt;
    .wrapper &#123;
        display:flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    &#125;
&lt;/style&gt;</code></pre>
<hr>
<SectionComponent tag="h2" 
  id="step-2-add-to-the-global-section-3"
  bind:intersecting={visibleById["step-2-add-to-the-global-section-3"]}
  onIntersectChange={(visible) => setVisible("step-2-add-to-the-global-section-3", visible)}
>Step 2: Add to the Global Section</SectionComponent>
<p>Register the component in the editor’s <a
  href="/Documentation/Defining_an_Editor/Global_Projections"
><code>global</code></a> section:</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase3/defs/main.edit#L3-L8

global &#123;
    external &#123;
        PersonIcon,
        PhoneButton
    &#125;
&#125;</code></pre>
<hr>
<SectionComponent tag="h2" 
  id="step-3-include-in-the-projection-4"
  bind:intersecting={visibleById["step-3-include-in-the-projection-4"]}
  onIntersectChange={(visible) => setVisible("step-3-include-in-the-projection-4", visible)}
>Step 3: Include in the Projection</SectionComponent>
<p>Use the wrapper in the <code>Person</code> projection by adding <code>wrap=PhoneButton</code> to the phone property.
We’ll keep this in the <code>externals.edit</code> projection set we introduced earlier:</p>
<pre class="language-freon"><code class="language-freon">// CourseSchedule/phase3/defs/externals.edit

editor externals

Person &#123;[
    [fragment nameAndIcon]
        Availability: $&#123;self.availability checkbox&#125; Competence: $&#123;self.competence&#125;
]
fragment nameAndIcon [
[external=PersonIcon] Nickname: $&#123;self.name&#125;
Full Name: $&#123;self.fullName&#125;
$&#123;self.phone wrap=PhoneButton&#125;
]
&#125;</code></pre>
<hr>
<SectionComponent tag="h2" 
  id="step-4-register-in-the-starter-code-5"
  bind:intersecting={visibleById["step-4-register-in-the-starter-code-5"]}
  onIntersectChange={(visible) => setVisible("step-4-register-in-the-starter-code-5", visible)}
>Step 4: Register in the Starter Code</SectionComponent>
<p>Tell Freon how to instantiate the component:</p>
<pre class="language-ts"><code class="language-ts">// CourseSchedule/phase3/src/external/externals.ts

import &#123;setCustomComponents&#125; from "@freon4dsl/core-svelte";
import PersonIcon from "./PersonIcon.svelte";
import PhoneButton from "./PhoneButton.svelte";

/**
 * Configure the external components used, so Freon can find them.
 */
export function configureExternals() &#123;
    setCustomComponents([
        &#123; component: PersonIcon, knownAs: "PersonIcon" &#125;,
        &#123; component: PhoneButton, knownAs: "PhoneButton" &#125;,
    ]);
&#125;</code></pre>
<hr>
<SectionComponent tag="h2" 
  id="final-result-6"
  bind:intersecting={visibleById["final-result-6"]}
  onIntersectChange={(visible) => setVisible("final-result-6", visible)}
>Final Result</SectionComponent>
<p>Your editor now shows a <strong>phone button</strong> next to each number. Clicking it opens a snackbar with the number:</p>
<Figure
imageName={'examples/CourseSchedule/Screenshot-step3.png'}
caption={'Editor with added Phone Button'}
figureNumber={1}
/>
<h3 id="conclusion-5">Conclusion</h3>
<p>You’ve added a <strong>custom wrapper component</strong> to the Freon editor.<br>
It wraps a phone number and includes a button that triggers a snackbar.<br>
From here, you can build richer integrations—dialers, validations, or external lookups—fully integrated with your Freon projections.</p>
<p>Next, you’ll learn how to <strong>replace</strong> the component that renders a list.</p>


<PrevNextSection {prevLink} {nextLink} />